import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pulls up to 6 most recent photos across all public albums for a
// homepage "latest from the studio" strip. Quiet, ever-changing.
export default function LatestFromStudio() {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: albums } = await axios.get(`${API}/gallery/albums`);
        const recent = [];
        for (const album of albums) {
          const { data } = await axios.get(`${API}/gallery/albums/${album.slug}`);
          for (const p of data.photos || []) {
            recent.push({ ...p, album_slug: album.slug, album_title: album.title });
          }
        }
        recent.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        if (!cancelled) setPhotos(recent.slice(0, 6));
      } catch {
        if (!cancelled) setPhotos([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (photos.length === 0) return null;

  return (
    <section className="py-20 bg-[#FAF9F6]" data-testid="latest-from-studio">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-caption text-[#84A98C] mb-2 inline-flex items-center gap-2">
              <Camera className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.5} />
              Latest from the studio
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#354F52] leading-tight">
              Quiet glimpses from the bench
            </h2>
          </div>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 text-[#354F52] hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-widest"
            data-testid="latest-view-all"
          >
            View the gallery
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {photos.map((p) => (
            <Link
              key={p.id}
              to={`/gallery/${p.album_slug}`}
              className="group relative aspect-square overflow-hidden rounded-sm bg-[#F5F5F0]"
              data-testid={`latest-photo-${p.id}`}
            >
              <img
                src={p.image_url}
                alt={p.alt_text || p.caption || p.album_title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {p.caption && (
                <span className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-[#354F52]/80 to-transparent text-[10px] text-[#FAF9F6] font-serif italic line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {p.caption}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
